/**
 * MenderPage — public product · full-stack · glass ledger + AI insurance-agent.
 *
 * Founder ruling 2026-08-11 late-evening:
 *   "Full stack, glass ledger, the whole works. But it's an insurance agent
 *    AI agent, not a chatbot."
 *
 * Layout, top to bottom:
 *   1. Hero · name + pledge line + industry-buried strip
 *   2. Insurance-Agent AI · one plain-language door · returns a real quote
 *   3. Form gallery · 15 forms · sterling clause vs industry baseline · sample quote
 *   4. Glass ledger · live premiums · claims paid · philanthropy owed
 *   5. Pledge scroll · plain-ink, court-strength, on the fold
 */
import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MenderPage() {
  const [forms, setForms] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [pledge, setPledge] = useState(null);
  const [openForm, setOpenForm] = useState(null);

  // Agent state
  const [intake, setIntake] = useState("");
  const [agentBusy, setAgentBusy] = useState(false);
  const [agentReply, setAgentReply] = useState(null);
  const [agentError, setAgentError] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/mender/forms`).then(r => r.json()).then(d => setForms(d.forms || [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/mender/ledger`).then(r => r.json()).then(setLedger).catch(() => {});
    fetch(`${BACKEND_URL}/api/mender/pledge`).then(r => r.json()).then(setPledge).catch(() => {});
    // Front 6 · pre-fill from ?intake= URL param so Sage-chat can deep-link
    // an insurance question straight into the agent.
    const params = new URLSearchParams(window.location.search);
    const preIntake = params.get("intake");
    if (preIntake) setIntake(preIntake.slice(0, 2000));
  }, []);

  const consultAgent = async () => {
    if (!intake.trim() || agentBusy) return;
    setAgentBusy(true);
    setAgentError(null);
    setAgentReply(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/mender/agent/consult`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intake: intake.trim() }),
      });
      const d = await r.json();
      if (!r.ok) {
        setAgentError(d.detail || `HTTP ${r.status}`);
      } else {
        setAgentReply(d);
      }
    } catch (e) {
      setAgentError(String(e));
    } finally {
      setAgentBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-ink" data-testid="mender-page">
      <TopNav />

      {/* Launch-priority banner — Founder ruling 2026-08-11 · Mender is product #3 */}
      <div className="bg-sage-accent text-sage-bg py-3 px-6 text-center" data-testid="mender-priority-banner">
        <p className="text-base md:text-lg font-bold uppercase tracking-wider">
          Launch Priority · Airtight standalone in progress
        </p>
        <p className="text-xs md:text-sm italic mt-1 opacity-90">
          The insurance company built to expose the industry.
        </p>
      </div>

      {/* ── SLOGAN + TIER MAP · Founder ruling 2026-08-11 ── */}
      <MenderTierSection />

      {/* ── HERO ── */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-3" data-testid="mender-eyebrow">
          The Mender · Family-owned universal cover
        </p>
        <div className="flex items-start gap-4 mb-4">
          <div className="text-sage-accent shrink-0"><ProductMark slug="mender" size={56} /></div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-tight" data-testid="mender-title">
            We mend what breaks. The industry denies what breaks.
          </h1>
        </div>
        <p className="text-lg sm:text-xl text-sage-ink/80 max-w-3xl leading-snug" data-testid="mender-subtitle">
          Every form of insurance that exists — priced at exactly 10% profit — and every dollar of that profit is pledged to philanthropy at each quarterly seal.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 text-xs uppercase tracking-widest text-sage-ink/60">
          <span className="border border-sage-line px-3 py-1.5" data-testid="mender-badge-forms">15 forms of cover on Day 1</span>
          <span className="border border-sage-line px-3 py-1.5" data-testid="mender-badge-margin">10% profit target · exact</span>
          <span className="border border-sage-line px-3 py-1.5" data-testid="mender-badge-pledge">100% of net profit pledged</span>
          <span className="border border-sage-line px-3 py-1.5" data-testid="mender-badge-court">Missouri circuit-court enforceable</span>
        </div>
      </section>

      {/* ── INSURANCE-AGENT AI ── */}
      <section className="max-w-5xl mx-auto px-6 py-10 border-t border-sage-line" data-testid="mender-agent-section">
        <p className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Insurance-agent AI · not a chatbot</p>
        <h2 className="text-2xl sm:text-3xl font-serif leading-tight mb-2">Tell the agent what you need, in your own words.</h2>
        <p className="text-sm text-sage-ink/70 mb-4">
          The agent reads your intake, extracts the form and coverage, and returns a real quote from the deterministic pricing engine — no invented numbers, no lecture.
        </p>
        <textarea
          value={intake}
          onChange={(e) => setIntake(e.target.value)}
          placeholder="I need home insurance for my house worth 300000 in Colorado, I'm 42."
          rows={3}
          className="w-full border border-sage-line bg-white p-4 text-base focus:border-sage-accent outline-none rounded-lg"
          data-testid="mender-agent-input"
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={consultAgent}
            disabled={agentBusy || !intake.trim()}
            className="rounded-lg bg-sage-ink text-sage-bg font-semibold py-2.5 px-5 hover:bg-sage-accent transition-colors disabled:opacity-40"
            data-testid="mender-agent-submit"
          >
            {agentBusy ? "Reading…" : "Ask the agent"}
          </button>
          {agentError && (
            <span className="text-sm text-red-600" data-testid="mender-agent-error">{agentError}</span>
          )}
        </div>

        {agentReply && (
          <div className="mt-6 border border-sage-line rounded-lg bg-sage-elevated p-5" data-testid="mender-agent-reply">
            <p className="text-xs uppercase tracking-widest text-sage-accent mb-2">Agent · reply</p>
            <p className="text-base leading-relaxed text-sage-ink mb-4">{agentReply.agent_speech}</p>
            {agentReply.quote && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-4" data-testid="mender-agent-quote">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sage-ink/60">Annual</p>
                  <p className="text-lg font-semibold tabular-nums">${agentReply.quote.annual_premium_usd.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sage-ink/60">Monthly</p>
                  <p className="text-lg font-semibold tabular-nums">${agentReply.quote.monthly_premium_usd.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sage-ink/60">Profit%</p>
                  <p className="text-lg font-semibold tabular-nums">{agentReply.quote.profit_pct_of_premium}%</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sage-ink/60">Pledged</p>
                  <p className="text-lg font-semibold tabular-nums">100%</p>
                </div>
              </div>
            )}
            {agentReply.sterling_clause_named && (
              <div className="text-xs">
                <p className="uppercase tracking-widest text-sage-accent mb-1">Sterling clause named</p>
                <p className="text-sage-ink/80 mb-2">{agentReply.sterling_clause_named}</p>
                <p className="uppercase tracking-widest text-sage-accent mb-1">Industry baseline beaten</p>
                <p className="text-sage-ink/60">{agentReply.industry_baseline_beaten}</p>
                {agentReply.buries && (
                  <p className="mt-2 text-[10px] uppercase tracking-widest text-sage-ink/50">Buries: {agentReply.buries}</p>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── FORM GALLERY ── */}
      <section className="max-w-5xl mx-auto px-6 py-10 border-t border-sage-line" data-testid="mender-forms-section">
        <p className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Fifteen forms · sterling vs industry</p>
        <h2 className="text-2xl sm:text-3xl font-serif leading-tight mb-4">Every form of cover, one shelf.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {forms.map((f) => (
            <button
              key={f.form}
              onClick={() => setOpenForm(openForm === f.form ? null : f.form)}
              className={`text-left border ${openForm === f.form ? "border-sage-accent" : "border-sage-line"} rounded-lg p-4 bg-white hover:border-sage-accent transition-colors`}
              data-testid={`mender-form-card-${f.form}`}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-lg font-serif">{f.name}</span>
                <span className="text-xs uppercase tracking-widest text-sage-accent">+{f.sterling_edge_pts}pts</span>
              </div>
              <p className="text-[11px] uppercase tracking-widest text-sage-ink/60 mb-1">Sterling {(f.sterling_loss_ratio * 100).toFixed(0)}% · Industry {(f.industry_loss_ratio * 100).toFixed(0)}%</p>
              <p className="text-[10px] uppercase tracking-widest text-sage-ink/50">Buries {f.buries_count} carriers</p>
            </button>
          ))}
        </div>

        {openForm && <FormDetail slug={openForm} />}
      </section>

      {/* ── GLASS LEDGER ── */}
      <section className="max-w-5xl mx-auto px-6 py-10 border-t border-sage-line" data-testid="mender-ledger-section">
        <p className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Glass ledger · live</p>
        <h2 className="text-2xl sm:text-3xl font-serif leading-tight mb-4">Every dollar in. Every dollar paid. Every dollar owed to philanthropy.</h2>
        {ledger && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="mender-ledger-tiles">
            <LedgerTile label="Premium collected" value={`$${ledger.totals.premium_collected_usd.toLocaleString()}`} />
            <LedgerTile label="Claims paid" value={`$${ledger.totals.claims_paid_usd.toLocaleString()}`} />
            <LedgerTile label="Loss ratio" value={ledger.totals.current_loss_ratio !== null ? `${(ledger.totals.current_loss_ratio * 100).toFixed(1)}%` : "—"} />
            <LedgerTile label="Net profit before pledge" value={`$${ledger.totals.net_profit_before_pledge_usd.toLocaleString()}`} />
            <LedgerTile label="Philanthropy owed (100%)" value={`$${ledger.totals.philanthropy_owed_usd.toLocaleString()}`} accent />
          </div>
        )}
        {ledger && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <div className="border border-sage-line rounded px-3 py-2" data-testid="mender-ledger-policies">Bound policies: <span className="tabular-nums font-semibold">{ledger.counters?.bound_policies ?? 0}</span></div>
            <div className="border border-sage-line rounded px-3 py-2" data-testid="mender-ledger-claims-filed">Claims filed: <span className="tabular-nums font-semibold">{ledger.counters?.claims_filed ?? 0}</span></div>
            <div className="border border-sage-line rounded px-3 py-2" data-testid="mender-ledger-claims-paid">Claims paid: <span className="tabular-nums font-semibold">{ledger.counters?.claims_paid ?? 0}</span></div>
          </div>
        )}
      </section>

      {/* ── PLEDGE ── */}
      <section className="max-w-5xl mx-auto px-6 py-10 border-t border-sage-line" data-testid="mender-pledge-section">
        <p className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">The pledge · plain ink · court-strength</p>
        {pledge && (
          <blockquote className="border-l-4 border-sage-accent pl-4 py-2 text-lg leading-relaxed text-sage-ink/90" data-testid="mender-pledge-text">
            {pledge.pledge}
          </blockquote>
        )}
        {pledge && (
          <p className="text-xs uppercase tracking-widest text-sage-ink/50 mt-3">
            {pledge.sealed_by} · sealed {pledge.sealed_at}
          </p>
        )}
      </section>
    </div>
  );
}

function LedgerTile({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-sage-accent bg-sage-elevated" : "border-sage-line bg-white"} rounded-lg p-4`}>
      <p className="text-[10px] uppercase tracking-widest text-sage-ink/60 mb-1">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function FormDetail({ slug }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/mender/forms/${slug}`).then(r => r.json()).then(setDetail).catch(() => {});
  }, [slug]);
  if (!detail) return null;
  return (
    <div className="mt-4 border border-sage-accent rounded-lg bg-white p-5" data-testid={`mender-form-detail-${slug}`}>
      <h3 className="text-xl font-serif mb-2">{detail.name}</h3>
      {detail.sample_quote?.line && (
        <p className="text-sm text-sage-ink/80 mb-3 italic" data-testid={`mender-form-sample-${slug}`}>
          {detail.sample_quote.line}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-sage-accent mb-2">Sterling clauses</p>
          <ul className="text-sm space-y-1 list-disc list-inside text-sage-ink/85">
            {(detail.sterling_clauses || []).map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-sage-ink/50 mb-2">Industry baseline</p>
          <ul className="text-sm space-y-1 list-disc list-inside text-sage-ink/55">
            {(detail.industry_baseline || []).map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      </div>
      {detail.buries?.length > 0 && (
        <p className="text-[10px] uppercase tracking-widest text-sage-ink/50 mt-4">
          Buries: {detail.buries.join(" · ")}
        </p>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// TIER MAP · every insurance type with target launch rates and
// timelines. Founder ruling 2026-08-11: "show all the types of
// insurance and our target launch rates with comparisons, but all
// tiers shown to the public as 'coming in x-y months.'"
// ─────────────────────────────────────────────────────────────
const TIER_MAP = [
  // ── TIER 1 · launch-ready · beta build now ────────────────────
  { tier: 1, line: "Renters", when: "0–6 months", avg_incumbent: 240, mender_target: 200, incumbents: "Lemonade $180 · State Farm $250 · Allstate $260", why: "Small policies, algorithmic pricing works, MGA-clean" },
  { tier: 1, line: "Pet", when: "0–6 months", avg_incumbent: 720, mender_target: 600, incumbents: "Lemonade Pet $580 · Trupanion $720 · Nationwide $800", why: "Growing 25%/yr, no medical-privacy law, no fronting resistance" },
  { tier: 1, line: "Cyber (SMB under $10M rev)", when: "0–6 months", avg_incumbent: 1500, mender_target: 1200, incumbents: "Coalition $1,400 · At-Bay $1,600 · Corvus $1,800", why: "25M uncovered SMBs · AI underwrites in 5 min · zero human sales cost" },
  // ── TIER 2 · add after 6 months ───────────────────────────────
  { tier: 2, line: "Term Life", when: "6–12 months", avg_incumbent: 480, mender_target: 400, incumbents: "Ladder $380 · Ethos $420 · Haven Life $460", why: "Actuarially simple · algorithmic pricing viable" },
  { tier: 2, line: "Small-Business BOP", when: "6–12 months", avg_incumbent: 1800, mender_target: 1500, incumbents: "Hiscox $1,900 · Next $2,100 · Chubb $2,400", why: "High per-policy revenue · high automation potential" },
  // ── TIER 3 · needs reinsurance treaty · 12–24 months ──────────
  { tier: 3, line: "Home (Wave 1 non-CAT: IA · MN · WI · OH · IN)", when: "12–18 months", avg_incumbent: 1400, mender_target: 1150, incumbents: "State Farm $1,500 · Allstate $1,450 · USAA $1,300", why: "Family disaster-relief architecture is the competitive weapon incumbents cannot copy" },
  { tier: 3, line: "Auto (starts after 12 months of Mender claim data)", when: "18–24 months", avg_incumbent: 1600, mender_target: 1300, incumbents: "GEICO $1,450 · Progressive $1,500 · State Farm $1,700", why: "$330B market · biggest sheer revenue play in P&C" },
  { tier: 3, line: "Workers Comp (46 non-monopoly states · waves of 10)", when: "18–24 months", avg_incumbent: 6500, mender_target: 5200, incumbents: "The Hartford · Travelers · Zurich · Berkshire's Guard", why: "20-point AI expense advantage · argues FOR the worker · 7-day approval" },
  // ── TIER 4 · never or via UBI rail ────────────────────────────
  { tier: 4, line: "Health insurance", when: "Never · ACA kill-zone", avg_incumbent: null, mender_target: null, incumbents: "—", why: "Medical Loss Ratio caps + $50M entry cost · not a Mender wedge" },
  { tier: 4, line: "Workers Comp in monopoly states (WA · ND · OH · WY)", when: "Never · illegal", avg_incumbent: null, mender_target: null, incumbents: "—", why: "State monopoly funds only · private carriers prohibited by law" },
  { tier: 4, line: "Long-term care", when: "Never · routes to UBI rail", avg_incumbent: null, mender_target: null, incumbents: "—", why: "Not insurance · social infrastructure · covered under family UBI/disabled/elderly" },
];

const TIER_LABEL = {
  1: { label: "LAUNCHING NOW · BETA BUILD", color: "sage-accent" },
  2: { label: "COMING 6–12 MONTHS", color: "sage-accent" },
  3: { label: "COMING 12–24 MONTHS · NEEDS REINSURANCE", color: "sage-warn" },
  4: { label: "NOT OFFERED · SEE WHY", color: "sage-muted" },
};

function MenderTierSection() {
  return (
    <section className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="mender-tier-section">
      {/* Slogan hero */}
      <div className="mb-10" data-testid="mender-slogan">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-3">The pledge, out loud</div>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter leading-tight text-sage-text mb-4">
          Mender: the cheapest insurance that pays the highest percentage of claims.
        </h2>
        <p className="text-xl md:text-2xl font-bold uppercase tracking-tight text-sage-accent">
          Compare and switch today.
        </p>
      </div>

      {/* Universal Bundle · $900/year hero */}
      <div className="border-2 border-sage-accent bg-sage-pale/30 p-6 md:p-8 mb-12" data-testid="mender-universal-bundle">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">The Mender Universal Bundle</div>
        <div className="grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="text-3xl md:text-4xl font-bold tracking-tight text-sage-ink mb-3 leading-tight">
              One flat rail. $900 a year. Renters + Pet + Cyber + Term Life.
            </div>
            <p className="text-sm text-sage-ink/80 sage-prose mb-3">
              The industry sells these separately at ~$3,000–5,000 combined. Mender bundles them at $900 flat because we cut out every middleman: no agents, no phone rooms, no commissions, no TV ads, no shareholder tax. We can. They can't.
            </p>
            <p className="text-xs italic text-sage-ink/70">Less than 10% of the industry combined price. Highest-payout claim design. One quote, one bill, one honest ledger.</p>
          </div>
          <div className="text-center border-l-2 border-sage-accent md:pl-6">
            <div className="text-6xl md:text-7xl font-bold tracking-tighter text-sage-accent">$900</div>
            <div className="text-xs uppercase tracking-[0.2em] text-sage-ink/60 mt-2">per year · flat</div>
            <div className="text-xs text-sage-ink/50 line-through mt-1">industry $3,000–5,000</div>
          </div>
        </div>
      </div>

      {/* Tier map · now labeled Approved Outline per Founder ruling */}
      <div className="mb-16">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Every line we cover · every line we don't · this is our Approved Outline</div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-6">Approved Outline · in ink</h2>
        <div className="space-y-6" data-testid="tier-map">
          {[1, 2, 3, 4].map((tier) => (
            <TierGroup key={tier} tier={tier} />
          ))}
        </div>
      </div>

      {/* Cyber wedge */}
      <CyberWedge />

      {/* Workers Comp design */}
      <WorkersCompDesign />

      {/* Home wave map */}
      <HomeWaveMap />

      {/* Reinsurance plan */}
      <ReinsurancePlan />

      {/* No-Ads Pledge · Founder ruling 2026-08-11 */}
      <NoAdsPledge />
    </section>
  );
}

function NoAdsPledge() {
  return (
    <div className="mb-8 border-2 border-sage-ink/20 p-6" data-testid="no-ads-pledge">
      <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Family-wide policy</div>
      <h3 className="text-xl md:text-2xl font-bold tracking-tight uppercase mb-3">No paid ads. Ever.</h3>
      <div className="text-sm text-sage-ink/80 sage-prose space-y-2">
        <p>We have no affiliate advertising revenue. We have no advertising budget.</p>
        <p>We will never make you watch an ad for any of our products. If ads appear on your screen, they were placed by the platform serving the page — not by us.</p>
        <p>The most we do: name our partners at the bottom of our own landing pages and our own in-house products. That's the whole shape of our promotion.</p>
      </div>
    </div>
  );
}

function TierGroup({ tier }) {
  const items = TIER_MAP.filter((i) => i.tier === tier);
  const meta = TIER_LABEL[tier];
  return (
    <div data-testid={`tier-${tier}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] border border-${meta.color} text-${meta.color} px-2 py-1 inline-block mb-3`}>
        Tier {tier} · {meta.label}
      </div>
      <div className="border border-sage-ink/20 divide-y divide-sage-ink/10">
        {items.map((i) => (
          <div key={i.line} className="grid md:grid-cols-12 gap-3 p-4 hover:bg-sage-pale/20" data-testid={`tier-line-${i.line.split(" ")[0].toLowerCase()}`}>
            <div className="md:col-span-5">
              <div className="text-base font-bold uppercase text-sage-ink">{i.line}</div>
              <div className="text-xs text-sage-ink/60 italic mt-1">{i.why}</div>
            </div>
            <div className="md:col-span-3 text-sm text-sage-ink/70">
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-ink/50">Timeline</div>
              <div>{i.when}</div>
            </div>
            <div className="md:col-span-4 text-sm text-sage-ink/70 text-right">
              {i.mender_target != null ? (
                <>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-ink/50">Mender · avg incumbent</div>
                  <div>
                    <span className="text-sage-accent font-bold">${i.mender_target}</span>
                    <span className="text-sage-ink/50"> · </span>
                    <span className="line-through">${i.avg_incumbent}</span>
                  </div>
                  <div className="text-xs text-sage-ink/50 mt-1">vs. {i.incumbents}</div>
                </>
              ) : (
                <div className="text-xs italic text-sage-ink/60">— not offered —</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CyberWedge() {
  return (
    <div className="mb-16 border-l-4 border-sage-accent pl-6" data-testid="cyber-wedge">
      <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">The Cyber wedge · plain English</div>
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-4">Cyber insurance for the plumber, the landscaper, the accountant</h2>
      <div className="text-base text-sage-ink/80 sage-prose space-y-3 max-w-3xl">
        <p>Big-company cyber insurance is a solved market. Coalition, At-Bay, Corvus, Beazley — they cover the Fortune 5,000.</p>
        <p><strong>Small businesses under $10M revenue get ignored.</strong> The $500–$2,000 annual premium isn't worth an incumbent's sales-team hour. Manual questionnaires take days. Small-business support tickets don't scale.</p>
        <p><strong>Meanwhile, 65% of ransomware victims are small businesses.</strong> 30 million US SMBs · only 15% have cyber insurance · roughly 25 million uncovered small businesses.</p>
        <p><strong>Mender's edge:</strong> AI underwriting quotes a $10M-revenue plumbing company in 5 minutes — from their domain, their email exposure, and a 90-second questionnaire. Zero human sales cost. Profitable at $500 annual premium where incumbents lose money below $10K.</p>
        <p className="text-sage-accent font-semibold">The wedge: "Cyber insurance for a $2M-revenue landscaper. Quote in 5 minutes. $600/year. Real policy. Real claims. Coverage the incumbents refused to sell you."</p>
      </div>
    </div>
  );
}

function WorkersCompDesign() {
  return (
    <div className="mb-16" data-testid="workers-comp-design">
      <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Workers Comp · the good-guy shape</div>
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-4">The insurance that argues FOR the worker</h2>
      <p className="text-base text-sage-ink/80 sage-prose mb-6 max-w-3xl">
        The 20-point AI expense advantage passes to the customer as lower price · and to the worker as generous adjudication.
        Traditional workers comp runs 60–90 day approvals with 12–18% denial rates. Mender runs 7-day approvals with under 3% denial.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <ReviewLayer n={1} title="Treating physician" body="The treating physician's initial diagnosis is accepted as prima facie truth. Their word carries weight, not suspicion." />
        <ReviewLayer n={2} title="Independent medical exam" body="Scheduled within 7 days · non-adversarial · a second look, not a challenge. Fraud markers are AI-checked, not lawyer-assumed." />
        <ReviewLayer n={3} title="AI triage" body="Compares both reviews, flags real fraud markers, and argues FOR the worker on ambiguous cases. Bias tuned to generosity, not denial." />
      </div>

      <div className="border-l-4 border-sage-accent pl-6 mb-6" data-testid="wc-language">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Legal escalation policy</div>
        <p className="text-sm text-sage-ink/80 sage-prose">
          Litigation is the absolute last resort. Five mediation channels exist between "claim submitted" and "lawyer involved." We are the insurance company that does not sue our customers by default.
        </p>
      </div>

      <div data-testid="wc-state-waves">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Approved Outline · state rollout · waves of 10 · six-month cadence</div>
        <ul className="text-sm text-sage-ink/80 sage-prose space-y-1">
          <li><strong>Approved Outline · Wave 1 (safest, lowest-cost jurisdictions):</strong> Missouri · Iowa · Kansas · Nebraska · Indiana · Kentucky · Tennessee · Arkansas · South Carolina · Wisconsin</li>
          <li><strong>Approved Outline · Wave 2:</strong> Michigan · Minnesota · Ohio (private-market side, not the monopoly fund) · Virginia · North Carolina · Georgia · Alabama · Mississippi · West Virginia · Oklahoma</li>
          <li><strong>Approved Outline · Wave 3+:</strong> high-cost jurisdictions (CA · NY · NJ · MA · IL) — entered only with real Mender claim data</li>
          <li><strong>NEVER:</strong> WA · ND · OH monopoly-fund · WY (four state monopolies · private carriers prohibited by law)</li>
        </ul>
      </div>
    </div>
  );
}

function ReviewLayer({ n, title, body }) {
  return (
    <div className="border border-sage-ink/20 p-4" data-testid={`review-layer-${n}`}>
      <div className="text-3xl font-bold text-sage-accent mb-2">{n}</div>
      <div className="text-base font-bold uppercase text-sage-ink mb-2">{title}</div>
      <div className="text-sm text-sage-ink/70 sage-prose">{body}</div>
    </div>
  );
}

function HomeWaveMap() {
  return (
    <div className="mb-16" data-testid="home-wave-map">
      <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Home insurance · non-CAT states first, in waves of 5</div>
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-4">Home is the prize · we launch it CAT-free first</h2>
      <p className="text-base text-sage-ink/80 sage-prose mb-6 max-w-3xl">
        Home has higher lifetime customer value than auto, and the family's disaster-relief architecture is a competitive weapon incumbents cannot copy. We enter home in the safest states first, wave by wave, only extending as reinsurance depth grows.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-sage-accent p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">Approved Outline · Wave 1 · launch first · least CAT exposure</div>
          <div className="text-sm text-sage-ink sage-prose">Iowa · Minnesota · Wisconsin · Ohio · Indiana</div>
        </div>
        <div className="border border-sage-ink/20 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2">Approved Outline · Wave 2</div>
          <div className="text-sm text-sage-ink/80 sage-prose">Michigan · Illinois · Missouri · Kansas · Nebraska</div>
        </div>
        <div className="border border-sage-ink/20 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2">Approved Outline · Wave 3</div>
          <div className="text-sm text-sage-ink/80 sage-prose">South Dakota · North Dakota · Montana · Idaho · Utah</div>
        </div>
        <div className="border border-sage-warn/40 p-4 bg-sage-warn/5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-warn mb-2">Never · top-15 CAT states</div>
          <div className="text-sm text-sage-ink/80 sage-prose">FL · LA · MS · TX · CA · OK · CO · AL · GA · SC · NC · TN · VA · AR · KY</div>
        </div>
      </div>
    </div>
  );
}

function ReinsurancePlan() {
  return (
    <div className="mb-8" data-testid="reinsurance-plan">
      <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">The reinsurance stack · earned honestly</div>
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-4">Who backs Mender · at each phase</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ReinPhase n={1} state="live" title="Fronting carrier" body="Trisura · Clear Blue · State National. Fronting carrier holds the paper. Mender writes the underwriting. No reinsurance needed at MGA layer." />
        <ReinPhase n={2} state="next" title="Hannover Re" body="First excess-of-loss treaty on retained book. Deeply MGA-friendly · Munich Re as second option. Signed when premium book hits $2M." />
        <ReinPhase n={3} state="future" title="RenaissanceRe · Munich Re" body="Specialty CAT covers on the Home book · quota-share on retained book. Signed when Mender becomes regulated insurer." />
        <ReinPhase n={4} state="future" title="Berkshire · Gen Re" body="Whole-book excess CAT covers when scale justifies it. Buffett takes the call at $100M+ premium · not before." />
      </div>
    </div>
  );
}

function ReinPhase({ n, state, title, body }) {
  const label = { live: "LIVE", next: "NEXT", future: "PLANNED" }[state];
  const color = { live: "sage-accent", next: "sage-accent", future: "sage-muted" }[state];
  return (
    <div className="border border-sage-ink/20 p-4" data-testid={`reinsurance-phase-${n}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="text-3xl font-bold text-sage-ink/40">{n}</div>
        <div className={`text-[10px] uppercase tracking-[0.15em] border border-${color} text-${color} px-2 py-0.5`}>{label}</div>
      </div>
      <div className="text-base font-bold uppercase text-sage-ink mb-2">{title}</div>
      <div className="text-xs text-sage-ink/70 sage-prose">{body}</div>
    </div>
  );
}
